import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  Chip,
  Alert,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CreditCard as CreditCardIcon,
  LocalAtm as CashIcon,
  AccountBalance as CheckIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Student, Fee, PreAssessedFee, PricingTier } from '../../types';
import { useData } from '../../contexts/DataContext';

interface CreateManualOrderDialogProps {
  open: boolean;
  onClose: () => void;
  onOrderCreated: (order: any) => void;
}

interface SelectedFee {
  feeId: string;
  feeName: string;
  feeCategory: string;
  quantity: number;
  pricingTier: PricingTier;
  unitPrice: number;
  totalPrice: number;
  isPreAssessed: boolean;
  preAssessedFeeId?: string;
}

export function CreateManualOrderDialog({
  open,
  onClose,
  onOrderCreated,
}: CreateManualOrderDialogProps) {
  const { students, fees, preAssessedFees, feeCategories } = useData();
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [selectedFees, setSelectedFees] = useState<SelectedFee[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'check' | 'card'>('cash');
  const [checkNumber, setCheckNumber] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  const [isOverridingTotal, setIsOverridingTotal] = useState(false);
  const [overriddenTotal, setOverriddenTotal] = useState<string>('');

  const selectedStudent = useMemo(() => {
    return students.find((s) => s.id === selectedStudentId);
  }, [students, selectedStudentId]);

  const studentPreAssessedFees = useMemo(() => {
    if (!selectedStudentId) return [];
    return preAssessedFees
      .filter((paf) => paf.studentId === selectedStudentId && paf.status === 'pending')
      .map((paf) => {
        const fee = fees.find((f) => f.id === paf.feeId);
        if (!fee) return null;
        return { ...paf, fee };
      })
      .filter((item) => item !== null) as (PreAssessedFee & { fee: Fee })[];
  }, [selectedStudentId, preAssessedFees, fees]);

  const availableCatalogFees = useMemo(() => {
    if (!selectedStudentId) return [];
    return fees.filter((f) => f.isActive);
  }, [selectedStudentId, fees]);

  const handleAddPreAssessedFee = (preAssessedFee: PreAssessedFee & { fee: Fee }) => {
    const unitPrice =
      preAssessedFee.pricingTier === 'full'
        ? preAssessedFee.fee.basePrice
        : preAssessedFee.pricingTier === 'reduced'
        ? preAssessedFee.fee.reducedPrice
        : preAssessedFee.fee.freePrice;

    const totalPrice = unitPrice * preAssessedFee.quantity;

    const category = feeCategories.find((c) => c.id === preAssessedFee.fee.categoryId);

    const newFee: SelectedFee = {
      feeId: preAssessedFee.feeId,
      feeName: preAssessedFee.fee.name,
      feeCategory: category?.name || 'General',
      quantity: preAssessedFee.quantity,
      pricingTier: preAssessedFee.pricingTier,
      unitPrice,
      totalPrice,
      isPreAssessed: true,
      preAssessedFeeId: preAssessedFee.id,
    };

    setSelectedFees([...selectedFees, newFee]);
  };

  const handleAddCatalogFee = (fee: Fee, pricingTier: PricingTier) => {
    const unitPrice =
      pricingTier === 'full'
        ? fee.basePrice
        : pricingTier === 'reduced'
        ? fee.reducedPrice
        : fee.freePrice;

    const category = feeCategories.find((c) => c.id === fee.categoryId);

    const newFee: SelectedFee = {
      feeId: fee.id,
      feeName: fee.name,
      feeCategory: category?.name || 'General',
      quantity: 1,
      pricingTier,
      unitPrice,
      totalPrice: unitPrice,
      isPreAssessed: false,
    };

    setSelectedFees([...selectedFees, newFee]);
  };

  const handleUpdateQuantity = (index: number, delta: number) => {
    const updated = [...selectedFees];
    const newQuantity = Math.max(1, updated[index].quantity + delta);
    updated[index].quantity = newQuantity;
    updated[index].totalPrice = updated[index].unitPrice * newQuantity;
    setSelectedFees(updated);
  };

  const handleRemoveFee = (index: number) => {
    setSelectedFees(selectedFees.filter((_, i) => i !== index));
  };

  const subtotal = selectedFees.reduce((sum, fee) => sum + fee.totalPrice, 0);
  const tax = subtotal * 0.08;
  const calculatedTotal = subtotal + tax;
  const total = isOverridingTotal && overriddenTotal ? parseFloat(overriddenTotal) : calculatedTotal;

  const handleSubmit = () => {
    if (!selectedStudent) {
      alert('Please select a student');
      return;
    }

    if (selectedFees.length === 0) {
      alert('Please add at least one fee');
      return;
    }

    if (paymentMethod === 'check' && !checkNumber.trim()) {
      alert('Please enter a check number');
      return;
    }

    if (paymentMethod === 'card' && !transactionId.trim()) {
      alert('Please enter a transaction ID');
      return;
    }

    const paymentMethodLabel =
      paymentMethod === 'cash'
        ? 'Cash'
        : paymentMethod === 'check'
        ? `Check #${checkNumber}`
        : `Card (${transactionId})`;

    const txnId =
      paymentMethod === 'cash'
        ? `CASH-${Date.now()}`
        : paymentMethod === 'check'
        ? `CHECK-${checkNumber}`
        : transactionId;

    const orderNumber = `ORD-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`;

    const order = {
      id: `order-${Date.now()}`,
      orderNumber,
      userId: selectedStudent.id,
      studentId: selectedStudent.id,
      userType: 'student' as const,
      userName: `${selectedStudent.firstName} ${selectedStudent.lastName}`,
      userEmail: selectedStudent.email,
      subtotal,
      tax,
      total,
      paymentStatus: 'completed' as const,
      paymentMethod: paymentMethodLabel,
      transactionId: txnId,
      notes: notes || `Manual order created by admin on ${new Date().toLocaleString()}`,
      createdAt: new Date(),
      completedAt: new Date(),
      items: selectedFees.map((fee, index) => ({
        id: `item-${Date.now()}-${index}`,
        orderId: `order-${Date.now()}`,
        feeId: fee.feeId,
        feeName: fee.feeName,
        feeCategory: fee.feeCategory,
        quantity: fee.quantity,
        pricingTier: fee.pricingTier,
        unitPrice: fee.unitPrice,
        totalPrice: fee.totalPrice,
        createdAt: new Date(),
      })),
    };

    onOrderCreated(order);
    handleClose();
  };

  const handleClose = () => {
    setSelectedStudentId('');
    setSelectedFees([]);
    setPaymentMethod('cash');
    setCheckNumber('');
    setTransactionId('');
    setNotes('');
    setIsOverridingTotal(false);
    setOverriddenTotal('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Create Manual Order</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <TextField
            select
            fullWidth
            label="Select Student"
            value={selectedStudentId}
            onChange={(e) => {
              setSelectedStudentId(e.target.value);
              setSelectedFees([]);
            }}
            required
          >
            <MenuItem value="">
              <em>Select a student</em>
            </MenuItem>
            {students.map((student) => (
              <MenuItem key={student.id} value={student.id}>
                {student.firstName} {student.lastName} - {student.studentIdNumber}
              </MenuItem>
            ))}
          </TextField>

          {selectedStudent && (
            <>
              <Alert severity="info">
                Student: {selectedStudent.firstName} {selectedStudent.lastName} (
                {selectedStudent.studentIdNumber})
              </Alert>

              {studentPreAssessedFees.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Pre-Assessed Fees (Pending)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Fee Name</TableCell>
                          <TableCell align="center">Quantity</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {studentPreAssessedFees.map((paf) => {
                          const unitPrice =
                            paf.pricingTier === 'full'
                              ? paf.fee.basePrice
                              : paf.pricingTier === 'reduced'
                              ? paf.fee.reducedPrice
                              : paf.fee.freePrice;
                          const totalPrice = unitPrice * paf.quantity;
                          const isAdded = selectedFees.some(
                            (sf) => sf.preAssessedFeeId === paf.id
                          );

                          return (
                            <TableRow key={paf.id}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">{paf.fee.name}</Typography>
                                  <Chip
                                    label={paf.pricingTier}
                                    size="small"
                                    sx={{ mt: 0.5, textTransform: 'capitalize' }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="center">{paf.quantity}</TableCell>
                              <TableCell align="right">${unitPrice.toFixed(2)}</TableCell>
                              <TableCell align="right">${totalPrice.toFixed(2)}</TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant={isAdded ? 'outlined' : 'contained'}
                                  onClick={() => handleAddPreAssessedFee(paf)}
                                  disabled={isAdded}
                                >
                                  {isAdded ? 'Added' : 'Add'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Add from Catalog
                </Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={availableCatalogFees}
                  getOptionLabel={(option) => `${option.name} - $${option.basePrice.toFixed(2)}`}
                  renderInput={(params) => (
                    <TextField {...params} label="Search and select a fee to add" />
                  )}
                  onChange={(_, value) => {
                    if (value) {
                      handleAddCatalogFee(value, 'full');
                    }
                  }}
                  value={null}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Box>

              {selectedFees.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Selected Fees
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Fee Name</TableCell>
                          <TableCell align="center">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedFees.map((fee, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">{fee.feeName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {fee.feeCategory}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateQuantity(index, -1)}
                                  disabled={fee.quantity <= 1}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography>{fee.quantity}</Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateQuantity(index, 1)}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell align="right">${fee.unitPrice.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600}>
                                ${fee.totalPrice.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveFee(index)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Subtotal:</Typography>
                        <Typography>${subtotal.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Tax (8%):</Typography>
                        <Typography>${tax.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography color="text.secondary" variant="body2">
                          Calculated Total:
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          ${calculatedTotal.toFixed(2)}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" fontWeight={700}>
                            Total:
                          </Typography>
                          {!isOverridingTotal && (
                            <Tooltip title="Override total amount">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setIsOverridingTotal(true);
                                  setOverriddenTotal(calculatedTotal.toFixed(2));
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {isOverridingTotal ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={overriddenTotal}
                              onChange={(e) => setOverriddenTotal(e.target.value)}
                              type="number"
                              inputProps={{ step: '0.01', min: '0' }}
                              sx={{ width: 120 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                setIsOverridingTotal(false);
                                setOverriddenTotal('');
                              }}
                              color="error"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="h6" fontWeight={700} color="primary">
                            ${total.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                      {isOverridingTotal && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Total amount is being overridden. The order will be recorded with this custom amount.
                        </Alert>
                      )}
                    </Stack>
                  </Box>
                </Box>
              )}

              {selectedFees.length > 0 && (
                <>
                  <Divider />

                  <FormControl component="fieldset">
                    <FormLabel component="legend">Payment Method</FormLabel>
                    <RadioGroup
                      value={paymentMethod}
                      onChange={(e) =>
                        setPaymentMethod(e.target.value as 'cash' | 'check' | 'card')
                      }
                    >
                      <FormControlLabel
                        value="cash"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CashIcon />
                            <Typography>Cash</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="check"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckIcon />
                            <Typography>Check</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="card"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCardIcon />
                            <Typography>Credit/Debit Card</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  {paymentMethod === 'check' && (
                    <TextField
                      fullWidth
                      label="Check Number"
                      value={checkNumber}
                      onChange={(e) => setCheckNumber(e.target.value)}
                      required
                    />
                  )}

                  {paymentMethod === 'card' && (
                    <TextField
                      fullWidth
                      label="Transaction ID"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      required
                      helperText="Enter the transaction ID from the payment processor"
                    />
                  )}

                  <TextField
                    fullWidth
                    label="Notes (Optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                    placeholder="Add any additional notes about this order..."
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedStudent || selectedFees.length === 0}
        >
          Create Order
        </Button>
      </DialogActions>
    </Dialog>
  );
}
